import React, { useEffect, useCallback } from "react";
import { getCalApi } from "@calcom/embed-react";
import { trackConversion } from "../../lib/analytics";

/**
 * Cal.com popup trigger — opens the "HuddleUp Intro Call" modal without
 * leaving the current page. Used as a subtle "Book a call" shortcut for
 * visitors who already know they want to talk (e.g. next to the header
 * CTA and inside the homepage final CTA).
 *
 * The child render prop receives an `onClick` handler so any element
 * (link, button, icon) can act as the trigger while keeping styling in
 * the parent.
 *
 * Props:
 *   calLink  — e.g. "huddleup/30min"
 *   children — render prop that receives `{ onClick }`
 */
const NAMESPACE = "huddleup-30min-popup";

export default function CalPopupTrigger({
  calLink = "huddleup/30min",
  children,
}) {
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cal = await getCalApi({ namespace: NAMESPACE });
        if (!mounted) return;

        cal("ui", {
          theme: "dark",
          cssVarsPerTheme: {
            light: { "cal-brand": "#C9A961" },
            dark: { "cal-brand": "#C9A961" },
          },
          hideEventTypeDetails: false,
          layout: "month_view",
        });

        cal("on", {
          action: "bookingSuccessful",
          callback: () => trackConversion("call-booked", "direct-popup"),
        });
      } catch {
        /* embed blocked — trigger silently no-ops */
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const openPopup = useCallback(
    async (ev) => {
      if (ev && typeof ev.preventDefault === "function") ev.preventDefault();
      try {
        const cal = await getCalApi({ namespace: NAMESPACE });
        cal("modal", { calLink });
      } catch {
        // Last-resort fallback — open in a new tab so the CTA never dies.
        window.open(`https://cal.com/${calLink}`, "_blank", "noopener,noreferrer");
      }
    },
    [calLink],
  );

  return children({ onClick: openPopup });
}
